import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { useCrypto } from './crypto';
import { api } from '../api/client';
import { encrypt, decrypt } from '@mysecureroom/crypto';

export type GoalStatus = 'active' | 'completed' | 'paused' | 'abandoned';

export interface Goal {
  id: string;
  title: string;
  description: string | null;
  life_area_id: string | null;
  target_date: string | null;
  status: GoalStatus;
  progress: number; // 0-100
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  milestones?: Milestone[];
}

export interface Milestone {
  id: string;
  goal_id: string;
  title: string;
  percentage: number; // Weight toward goal (0-100)
  due_date: string | null;
  completed_at: string | null;
  sort_order: number;
}

interface GoalsState {
  goals: Goal[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface GoalsContextValue extends GoalsState {
  initialize: () => Promise<void>;
  createGoal: (input: {
    title: string;
    description?: string;
    targetDate?: string;
    lifeAreaId?: string;
  }) => Promise<Goal>;
  updateGoal: (id: string, updates: {
    title?: string;
    description?: string | null;
    status?: GoalStatus;
    progress?: number;
    targetDate?: string | null;
  }) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  addMilestone: (goalId: string, title: string, percentage: number, dueDate?: string) => Promise<Milestone>;
  updateMilestonePercentage: (goalId: string, milestoneId: string, percentage: number) => Promise<void>;
  toggleMilestoneComplete: (goalId: string, milestoneId: string) => Promise<void>;
  deleteMilestone: (goalId: string, milestoneId: string) => Promise<void>;
  loadMilestones: (goalId: string) => Promise<Milestone[]>;
  activeGoals: Goal[];
  completedGoals: Goal[];
  clear: () => void;
}

const GoalsContext = createContext<GoalsContextValue | null>(null);

export function GoalsProvider({ children }: { children: ReactNode }) {
  const crypto = useCrypto();
  const [state, setState] = useState<GoalsState>({
    goals: [],
    loading: false,
    error: null,
    initialized: false,
  });

  const encryptText = async (text: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return encrypt(crypto.encryptionKey, text);
  };

  const decryptText = async (encrypted: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return decrypt(crypto.encryptionKey, encrypted);
  };

  const calculateGoalProgress = (milestones: Milestone[]): number => {
    if (milestones.length === 0) return 0;
    const totalWeight = milestones.reduce((sum, m) => sum + m.percentage, 0);
    if (totalWeight === 0) return 0;
    const completedWeight = milestones
      .filter(m => m.completed_at !== null)
      .reduce((sum, m) => sum + m.percentage, 0);
    return Math.round((completedWeight / totalWeight) * 100);
  };

  const initialize = async () => {
    // Don't initialize if encryption key isn't ready yet
    if (!crypto.encryptionKey) {
      console.log('Waiting for encryption key...');
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const result = await api.get<{ goals: Goal[] }>('/goals');

      const decryptedGoals = await Promise.all(
        result.goals.map(async (g) => ({
          ...g,
          title: await decryptText(g.title),
          description: g.description ? await decryptText(g.description) : null,
        }))
      );

      setState({
        goals: decryptedGoals,
        loading: false,
        error: null,
        initialized: true,
      });
    } catch (error) {
      console.error('Failed to initialize goals:', error);
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load goals',
        initialized: true, // Prevent infinite retry loops
      }));
    }
  };

  const createGoal = async (input: {
    title: string;
    description?: string;
    targetDate?: string;
    lifeAreaId?: string;
  }): Promise<Goal> => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const encryptedTitle = await encryptText(input.title);
      const encryptedDescription = input.description ? await encryptText(input.description) : undefined;

      const result = await api.post<{ goal: Goal }>('/goals', {
        title: encryptedTitle,
        description: encryptedDescription,
        target_date: input.targetDate,
        life_area_id: input.lifeAreaId,
      });

      const decryptedGoal = {
        ...result.goal,
        title: await decryptText(result.goal.title),
        description: result.goal.description ? await decryptText(result.goal.description) : null,
      };

      setState(s => ({
        ...s,
        goals: [...s.goals, decryptedGoal],
        loading: false,
      }));

      return decryptedGoal;
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to create goal',
      }));
      throw error;
    }
  };

  const updateGoal = async (id: string, updates: {
    title?: string;
    description?: string | null;
    status?: GoalStatus;
    progress?: number;
    targetDate?: string | null;
  }) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const encryptedUpdates: any = { ...updates };
      if (updates.title) encryptedUpdates.title = await encryptText(updates.title);
      if (updates.description) encryptedUpdates.description = await encryptText(updates.description);

      const result = await api.patch<{ goal: Goal }>(`/goals/${id}`, encryptedUpdates);

      const decryptedGoal = {
        ...result.goal,
        title: await decryptText(result.goal.title),
        description: result.goal.description ? await decryptText(result.goal.description) : null,
      };

      setState(s => ({
        ...s,
        goals: s.goals.map(g => g.id === id ? decryptedGoal : g),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to update goal',
      }));
    }
  };

  const deleteGoal = async (id: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      await api.delete(`/goals/${id}`);
      setState(s => ({
        ...s,
        goals: s.goals.filter(g => g.id !== id),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to delete goal',
      }));
    }
  };

  const loadMilestones = async (goalId: string): Promise<Milestone[]> => {
    try {
      const result = await api.get<{ milestones: Milestone[] }>(`/goals/${goalId}/milestones`);

      const decryptedMilestones = await Promise.all(
        result.milestones.map(async (m) => ({
          ...m,
          title: await decryptText(m.title),
        }))
      );

      setState(s => ({
        ...s,
        goals: s.goals.map(g =>
          g.id === goalId ? { ...g, milestones: decryptedMilestones } : g
        ),
      }));

      return decryptedMilestones;
    } catch (error) {
      throw error;
    }
  };

  const addMilestone = async (goalId: string, title: string, percentage: number, dueDate?: string): Promise<Milestone> => {
    try {
      const encryptedTitle = await encryptText(title);
      const result = await api.post<{ milestone: Milestone }>(`/goals/${goalId}/milestones`, {
        title: encryptedTitle,
        percentage,
        due_date: dueDate,
      });

      const decryptedMilestone = {
        ...result.milestone,
        title: await decryptText(result.milestone.title),
      };

      setState(s => ({
        ...s,
        goals: s.goals.map(g =>
          g.id === goalId
            ? { ...g, milestones: [...(g.milestones || []), decryptedMilestone] }
            : g
        ),
      }));

      return decryptedMilestone;
    } catch (error) {
      throw error;
    }
  };

  const updateMilestonePercentage = async (goalId: string, milestoneId: string, percentage: number) => {
    try {
      await api.patch(`/goals/${goalId}/milestones/${milestoneId}`, { percentage });

      setState(s => ({
        ...s,
        goals: s.goals.map(g => {
          if (g.id !== goalId || !g.milestones) return g;
          const updatedMilestones = g.milestones.map(m =>
            m.id === milestoneId ? { ...m, percentage } : m
          );
          return {
            ...g,
            milestones: updatedMilestones,
            progress: calculateGoalProgress(updatedMilestones),
          };
        }),
      }));
    } catch (error) {
      throw error;
    }
  };

  const toggleMilestoneComplete = async (goalId: string, milestoneId: string) => {
    try {
      const goal = state.goals.find(g => g.id === goalId);
      const milestone = goal?.milestones?.find(m => m.id === milestoneId);
      if (!milestone) return;

      const completed_at = milestone.completed_at ? null : new Date().toISOString();

      await api.patch(`/goals/${goalId}/milestones/${milestoneId}`, { completed_at });

      setState(s => ({
        ...s,
        goals: s.goals.map(g => {
          if (g.id !== goalId || !g.milestones) return g;
          const updatedMilestones = g.milestones.map(m =>
            m.id === milestoneId ? { ...m, completed_at } : m
          );
          return {
            ...g,
            milestones: updatedMilestones,
            progress: calculateGoalProgress(updatedMilestones),
          };
        }),
      }));
    } catch (error) {
      throw error;
    }
  };

  const deleteMilestone = async (goalId: string, milestoneId: string) => {
    try {
      await api.delete(`/goals/${goalId}/milestones/${milestoneId}`);

      setState(s => ({
        ...s,
        goals: s.goals.map(g =>
          g.id === goalId && g.milestones
            ? {
                ...g,
                milestones: g.milestones.filter(m => m.id !== milestoneId),
                progress: calculateGoalProgress(g.milestones.filter(m => m.id !== milestoneId)),
              }
            : g
        ),
      }));
    } catch (error) {
      throw error;
    }
  };

  const clear = () => {
    setState({
      goals: [],
      loading: false,
      error: null,
      initialized: false,
    });
  };

  const activeGoals = useMemo(() =>
    state.goals.filter(g => g.status === 'active'),
    [state.goals]
  );

  const completedGoals = useMemo(() =>
    state.goals.filter(g => g.status === 'completed'),
    [state.goals]
  );

  const value: GoalsContextValue = {
    ...state,
    initialize,
    createGoal,
    updateGoal,
    deleteGoal,
    addMilestone,
    updateMilestonePercentage,
    toggleMilestoneComplete,
    deleteMilestone,
    loadMilestones,
    activeGoals,
    completedGoals,
    clear,
  };

  return <GoalsContext.Provider value={value}>{children}</GoalsContext.Provider>;
}

export function useGoals() {
  const context = useContext(GoalsContext);
  if (!context) {
    throw new Error('useGoals must be used within GoalsProvider');
  }
  return context;
}
